/**
 * THE THREE THINGS EVERY SCREEN DOES: ask, wait, and say what went wrong.
 *
 * `useAsk` is the only way a screen reads. It keeps the four states a screen
 * must draw separately — loading, loaded, empty, refused — instead of letting
 * each screen invent its own, and it re-asks when its inputs change.
 *
 * `useRun` is the only way a screen writes. It keeps the retry key of a submit
 * that timed out and RESENDS THE SAME ONE (DEF-088): a second key would be a
 * second departure, a second payment, a second piece of paper.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError, explain } from './api.js';

export interface Asked<T> { data: T | null; loading: boolean; error: ReturnType<typeof explain> | null; reload: () => void }

export function useAsk<T>(ask: () => Promise<T>, deps: readonly unknown[], messages: Record<string, string>): Asked<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<ReturnType<typeof explain> | null>(null);
  const [tick, setTick] = useState<number>(0);
  const live = useRef<boolean>(true);

  useEffect(() => {
    live.current = true;
    // `mine` belongs to THIS invocation alone. The shared ref cannot tell a
    // stale response from the current one: type into a search box and the
    // heavier unfiltered answer can land AFTER the filtered one, silently
    // putting the whole register back (found by the browser suite, 18 Sep).
    let mine = true;
    setLoading(true);
    ask().then(
      (v) => { if (mine && live.current) { setData(v); setError(null); setLoading(false); } },
      (e: unknown) => { if (mine && live.current) { setError(explain(e, messages)); setLoading(false); if (e instanceof ApiError && e.signedOut) signedOut(); } },
    );
    return () => { mine = false; live.current = false; };
    // eslint-disable-next-line
  }, [...deps, tick]);

  return { data, loading, error, reload: useCallback(() => setTick((t: number) => t + 1), []) };
}

export interface Runner {
  run: (fn: (retryKey?: string) => Promise<unknown>) => Promise<boolean>;
  busy: boolean;
  error: ReturnType<typeof explain> | null;
  clear: () => void;
}

export function useRun(messages: Record<string, string>): Runner {
  const [busy, setBusy] = useState<boolean>(false);
  const [error, setError] = useState<ReturnType<typeof explain> | null>(null);
  // DEF-088 — the key of an attempt that did not answer is KEPT here, and the
  // next attempt sends the same one. Minting a fresh key on retry is how one
  // lorry departs twice.
  const keptKey = useRef<string | undefined>(undefined);

  const run = useCallback(async (fn: (retryKey?: string) => Promise<unknown>): Promise<boolean> => {
    setBusy(true);
    try {
      const result = await fn(keptKey.current) as { idempotencyKey?: string } | undefined;
      keptKey.current = undefined;
      setError(null);
      setBusy(false);
      return true;
    } catch (e: unknown) {
      if (e instanceof ApiError) {
        // the server answered: this attempt is over, whatever it said
        keptKey.current = undefined;
        if (e.signedOut) signedOut();
      } else if (e instanceof Error && typeof (e as { retryKey?: string }).retryKey === 'string') {
        keptKey.current = (e as { retryKey?: string }).retryKey;
      }
      setError(explain(e, messages));
      setBusy(false);
      return false;
    }
  }, [messages]);

  return { run, busy, error, clear: useCallback(() => setError(null), []) };
}

/** The session ended: back to sign-in, remembering where the person was. */
function signedOut(): void {
  const here = globalThis.location?.hash ?? '';
  if (here && here !== '#/sign-in') sessionStorage.setItem('cts2.after-sign-in', here);
  if (globalThis.location) globalThis.location.hash = '#/sign-in';
}

/** Where to go back to after signing in again. */
export function whereTheyWere(): string {
  const saved = sessionStorage.getItem('cts2.after-sign-in');
  sessionStorage.removeItem('cts2.after-sign-in');
  return saved && saved.startsWith('#/') ? saved : '#/home';
}

/**
 * The hash route, as its parts: `#/orders/12` → ['orders', '12'].
 *
 * A hash may carry a query — `#/dispatch/new?order=12` is how one screen hands
 * the next screen its subject — and that query is NOT part of the path. Before
 * this split it, `rest[0]` was the string `new?order=12`, which matched nothing,
 * so the button on the order screen opened a challan numbered NaN (DEF-128,
 * found by the browser test).
 */
export function readRoute(hash: string): { parts: string[]; query: Record<string, string> } {
  const [path, search = ''] = hash.replace(/^#\/?/, '').split('?', 2);
  const query: Record<string, string> = {};
  for (const pair of search.split('&')) {
    if (!pair) continue;
    const [k, v = ''] = pair.split('=', 2);
    query[decodeURIComponent(k!)] = decodeURIComponent(v);
  }
  return { parts: (path ?? '').split('/').filter(Boolean), query };
}

export function useRoute(): string[] {
  const read = () => readRoute(globalThis.location?.hash ?? '#/home').parts;
  const [parts, setParts] = useState<string[]>(read);
  useEffect(() => {
    const onChange = () => setParts(read());
    globalThis.addEventListener?.('hashchange', onChange);
    return () => globalThis.removeEventListener?.('hashchange', onChange);
  }, []);
  return parts.length ? parts : ['home'];
}

/** What the previous screen asked for, by name, from the hash's query. */
export function routeQuery(name: string): string | null {
  return readRoute(globalThis.location?.hash ?? '').query[name] ?? null;
}

/**
 * A QUERY VALUE FROM THE ADDRESS, KEPT IN STEP WITH IT.
 *
 * `routeQuery` reads once. That is right for a first render and WRONG for a screen somebody
 * links INTO from somewhere else: `useRoute` re-renders on the path's parts, so a link that
 * changes only the query — `#/casework?case=12` while already on `#/casework` — leaves the
 * component mounted, its `useState` initialiser never runs again, and the person lands on
 * the list instead of the thing they clicked.
 *
 * Found by the browser suite on the workbench: every link from a passport, a customer or a
 * challan to one damage case opened the queue. It is the DEF-253 shape — what the URL says
 * and what the screen shows drifting apart — so it is fixed here, once, rather than with a
 * hashchange listener in each screen that needs it.
 */
export function useRouteQuery(name: string): string | null {
  const read = () => readRoute(globalThis.location?.hash ?? '').query[name] ?? null;
  const [value, setValue] = useState<string | null>(read);
  useEffect(() => {
    const onChange = () => setValue(read());
    globalThis.addEventListener?.('hashchange', onChange);
    // and once on mount, in case the hash moved between the initialiser and this effect
    onChange();
    return () => globalThis.removeEventListener?.('hashchange', onChange);
    // eslint-disable-next-line
  }, [name]);
  return value;
}

export function go(to: string): void {
  if (globalThis.location) globalThis.location.hash = to.startsWith('#') ? to : `#/${to.replace(/^\//, '')}`;
}
